import {
  fromPatientReferralData,
  fromPatientReferralDataList,
  toPatientReferralRequest,
  toPatientReferralUpdate,
} from '@medprompt/shared';

function asPatientReferralList(data) {
  return Array.isArray(data) ? fromPatientReferralDataList(data) : [];
}

/**
 * ReferralService - Handles patient referral API operations
 */
export class ReferralService {
  constructor(apiClient) {
    this.apiClient = apiClient;
    console.log('ReferralService initialized');
  }

  /**
   * Create a new patient referral
   * @param {Object} request - PatientReferralRequest { text, referralType, nhsId, referredAt }
   * @returns {Promise<void>}
   */
  async createReferral(request) {
    return this.apiClient.post('/api/referral/create', toPatientReferralRequest(request));
  }

  /**
   * Update an existing referral (null fields are ignored by the server)
   * @param {Object} update - PatientReferralUpdate { referralId, text?, referralType? }
   * @returns {Promise<void>}
   */
  async updateReferral(update) {
    return this.apiClient.post('/api/referral/update', toPatientReferralUpdate(update));
  }

  /**
   * Mark a referral as complete
   * @param {string} referralId
   * @returns {Promise<void>}
   */
  async completeReferral(referralId) {
    return this.apiClient.post(`/api/referral/${referralId}/complete`);
  }

  /**
   * Delete a referral
   * @param {string} referralId
   * @returns {Promise<void>}
   */
  async deleteReferral(referralId) {
    return this.apiClient.post(`/api/referral/${referralId}/delete`);
  }

  /**
   * Fetch active (incomplete) referrals for the current user
   * @returns {Promise<Array>} Active referrals
   */
  async fetchActiveReferrals(options = {}) {
    try {
      const data = await this.apiClient.get('/api/referrals/active/fetch', options);
      if (data === null || data === undefined) {
        console.error('fetchActiveReferrals received null/undefined data');
        return [];
      }
      return asPatientReferralList(data);
    } catch (error) {
      console.error('Error fetching active referrals:', error);
      throw error;
    }
  }

  /**
   * Fetch archived (completed) referrals for the current user
   * @returns {Promise<Array>} Archived referrals
   */
  async fetchArchiveReferrals(options = {}) {
    try {
      const data = await this.apiClient.get('/api/referrals/archive/fetch', options);
      if (data === null || data === undefined) {
        console.error('fetchArchiveReferrals received null/undefined data');
        return [];
      }
      return asPatientReferralList(data);
    } catch (error) {
      console.error('Error fetching archive referrals:', error);
      throw error;
    }
  }

  /**
   * Fetch pending (incomplete) referrals for the patient linked to a document.
   * NOTE: patientRecordData.pendingReferrals already provides this data; this method
   * exists for completeness and targeted one-shot fetches if needed.
   * @param {string} documentId
   * @returns {Promise<Array>} Pending referrals for the document's patient
   */
  async fetchDocumentPatientReferrals(documentId) {
    try {
      const data = await this.apiClient.get(`/api/document/${documentId}/patient/referrals/fetch`);
      if (data === null || data === undefined) {
        return [];
      }
      return asPatientReferralList(data);
    } catch (error) {
      console.error('Error fetching document patient referrals:', error);
      throw error;
    }
  }

  /**
   * Search for referrals
   * @param {string} query 
   * @returns {Promise<Array>} Search results
   */
  async fetchReferral(referralId, options = {}) {
    const data = await this.apiClient.get(`/api/referral/${referralId}`, options);
    return fromPatientReferralData(data);
  }

  async searchReferrals(query, options = {}) {
    const requestBody = { query: query };
    const data = await this.apiClient.post('/api/referrals/search', requestBody, options);
    return asPatientReferralList(data);
  }
}
